// ==========================================================================
// Projects Page - Gallery to Overlay Transition
// ==========================================================================

import gsap from "gsap";
import { Flip } from "gsap/all";

gsap.registerPlugin(Flip);

gsap.defaults({
    ease: "power3.out",
    duration: 0.6
});

/**
 * Initialize Projects Page
 */
export function initProjectsPage() {
    const section = document.querySelector(".projects-section");
    if (!section) return;

    const titleItems = document.querySelectorAll(".projects-title-item");
    const imageItems = document.querySelectorAll(".projects-img-item");
    const overlayItems = document.querySelectorAll(".projects-overlay-item");
    const overlay = document.querySelector(".projects-overlay");
    const overlayNav = document.querySelector(".projects-overlay-nav");
    const closeButton = document.querySelector("[data-overlay-close]");
    const prevButton = document.querySelector("[data-overlay-prev]");
    const nextButton = document.querySelector("[data-overlay-next]");
    const imageStack = document.querySelector(".projects-img-stack");

    // Get page offset to calculate global indices
    const projectsMain = document.querySelector(".projects-main");
    const pageOffset = projectsMain ? parseInt(projectsMain.dataset.pageOffset || 0) : 0;

    let activeIndex = null; // Global index
    let isTransitioning = false;

    /**
     * Get local index from global index
     */
    function getLocalIndex(globalIndex) {
        return globalIndex - pageOffset;
    }

    /**
     * Check if global index is on current page
     */
    function isOnCurrentPage(globalIndex) {
        const localIndex = getLocalIndex(globalIndex);
        return localIndex >= 0 && localIndex < titleItems.length;
    }

    /**
     * Update navigation buttons state
     */
    function updateNavButtons() {
        if (prevButton) {
            prevButton.disabled = activeIndex === 0;
        }
        if (nextButton) {
            nextButton.disabled = activeIndex === overlayItems.length - 1;
        }
    }

    /**
     * Show image on hover (local index)
     */
    function showImage(localIndex) {
        imageItems.forEach((item, i) => {
            item.classList.toggle("is-active", i === localIndex);
        });
    }

    /**
     * Reset image visibility (show first image)
     */
    function resetImages() {
        imageItems.forEach((item, i) => {
            item.classList.toggle("is-active", i === 0);
        });
    }

    /**
     * Open overlay for project (using global index)
     */
    function openOverlay(globalIndex, useFlip = true) {
        activeIndex = globalIndex;
        const localIndex = getLocalIndex(globalIndex);
        const onCurrentPage = isOnCurrentPage(globalIndex);

        const overlayItem = overlayItems[globalIndex];
        if (!overlayItem) return;

        // Show overlay
        overlay.classList.add("is-active");
        overlayItem.classList.add("is-active");

        // Update nav buttons
        updateNavButtons();

        // If opening from current page, use Flip animation
        if (onCurrentPage && useFlip && titleItems[localIndex]) {
            const titleBtn = titleItems[localIndex].querySelector(".projects-title-btn");
            const titleText = titleBtn?.querySelector(".projects-title-text");
            const image = imageItems[localIndex]?.querySelector(".projects-img");
            const textTarget = overlayItem.querySelector("[data-overlay-text]");
            const imgTarget = overlayItem.querySelector("[data-overlay-img]");

            // Hide the overlay's own title/image (we'll use the flipped ones)
            const overlayTitle = textTarget.querySelector(".projects-title-text--overlay");
            const overlayImage = imgTarget.querySelector(".projects-img--overlay");
            if (overlayTitle) overlayTitle.style.display = "none";
            if (overlayImage) overlayImage.style.display = "none";

            if (titleText && image) {
                // Record states before move
                const titleState = Flip.getState(titleText, { props: "fontSize" });
                const imageState = Flip.getState(image);

                // Move elements to overlay
                textTarget.appendChild(titleText);
                imgTarget.appendChild(image);

                // Animate with Flip
                Flip.from(titleState, { duration: 0.6 });
                Flip.from(imageState, { duration: 0.6 });
            }

            // Hide other titles on current page
            titleItems.forEach((item, i) => {
                if (i !== localIndex) {
                    const text = item.querySelector(".projects-title-text");
                    if (text) {
                        gsap.to(text, {
                            yPercent: 100,
                            autoAlpha: 0,
                            duration: 0.4,
                            delay: 0.1 * (titleItems.length - 1 - i)
                        });
                    }
                }
            });
        } else {
            // For projects not on current page, just fade in the overlay content
            const textTarget = overlayItem.querySelector("[data-overlay-text]");
            const imgTarget = overlayItem.querySelector("[data-overlay-img]");

            gsap.fromTo(textTarget,
                { autoAlpha: 0, y: 20 },
                { autoAlpha: 1, y: 0, duration: 0.4 }
            );
            gsap.fromTo(imgTarget,
                { autoAlpha: 0, y: 20 },
                { autoAlpha: 1, y: 0, duration: 0.4, delay: 0.1 }
            );
        }

        // Animate overlay content
        const content = overlayItem.querySelector(".projects-overlay-content");
        gsap.fromTo(content,
            { autoAlpha: 0, y: 30 },
            { autoAlpha: 1, y: 0, delay: 0.4 }
        );

        // Show nav
        gsap.to(overlayNav, {
            autoAlpha: 1,
            y: 0,
            delay: 0.5,
            duration: 0.4
        });

        // Hide image stack
        if (imageStack) {
            imageStack.classList.add("is-hidden");
        }

        // Prevent body scroll
        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";
        if (window.lenis) {
            window.lenis.stop();
        }

        // Scroll overlay to top
        overlay.scrollTop = 0;
    }

    /**
     * Switch to a different project in overlay (uses global indices)
     */
    function switchToProject(newIndex) {
        if (isTransitioning || activeIndex === null || newIndex === activeIndex) return;
        if (newIndex < 0 || newIndex >= overlayItems.length) return;

        isTransitioning = true;

        const currentOverlayItem = overlayItems[activeIndex];
        const newOverlayItem = overlayItems[newIndex];

        // Get current elements from overlay
        const currentTitleWrap = currentOverlayItem.querySelector("[data-overlay-text]");
        const currentImgWrap = currentOverlayItem.querySelector("[data-overlay-img]");
        const currentContent = currentOverlayItem.querySelector(".projects-overlay-content");

        // Check if current project had flipped elements from page
        const currentLocalIndex = getLocalIndex(activeIndex);
        const currentOnPage = isOnCurrentPage(activeIndex);
        const flippedTitleText = currentOnPage ? currentTitleWrap.querySelector(".projects-title-text:not(.projects-title-text--overlay)") : null;
        const flippedImage = currentOnPage ? currentImgWrap.querySelector(".projects-img:not(.projects-img--overlay)") : null;

        // Animate out current title and image
        const direction = newIndex > activeIndex ? -1 : 1; // -1 for next, 1 for prev

        gsap.to(currentTitleWrap, {
            autoAlpha: 0,
            x: 50 * direction,
            duration: 0.3
        });

        gsap.to(currentImgWrap, {
            autoAlpha: 0,
            x: 30 * direction,
            duration: 0.3
        });

        // Animate out current content
        gsap.to(currentContent, {
            autoAlpha: 0,
            y: -20,
            duration: 0.3
        });

        // Hide current overlay item after short delay
        setTimeout(() => {
            // Return flipped elements to their original positions (if they came from page)
            if (flippedTitleText && titleItems[currentLocalIndex]) {
                const originalTitleBtn = titleItems[currentLocalIndex].querySelector(".projects-title-btn");
                originalTitleBtn.insertBefore(flippedTitleText, originalTitleBtn.firstChild);
                gsap.set(flippedTitleText, { yPercent: 100, autoAlpha: 0 });

                // Show back the overlay's own title
                const overlayTitle = currentTitleWrap.querySelector(".projects-title-text--overlay");
                if (overlayTitle) overlayTitle.style.display = "";
            }
            if (flippedImage && imageItems[currentLocalIndex]) {
                imageItems[currentLocalIndex].appendChild(flippedImage);

                // Show back the overlay's own image
                const overlayImage = currentImgWrap.querySelector(".projects-img--overlay");
                if (overlayImage) overlayImage.style.display = "";
            }

            // Reset current item transforms
            gsap.set(currentTitleWrap, { autoAlpha: 1, x: 0 });
            gsap.set(currentImgWrap, { autoAlpha: 1, x: 0 });
            gsap.set(currentContent, { autoAlpha: 1, y: 0 });

            currentOverlayItem.classList.remove("is-active");

            // Update active index
            activeIndex = newIndex;
            updateNavButtons();

            // Get new overlay elements
            const newTextTarget = newOverlayItem.querySelector("[data-overlay-text]");
            const newImgTarget = newOverlayItem.querySelector("[data-overlay-img]");
            const newContent = newOverlayItem.querySelector(".projects-overlay-content");

            // Show new overlay item
            newOverlayItem.classList.add("is-active");

            // Animate in new title
            gsap.fromTo(newTextTarget,
                { autoAlpha: 0, x: -50 * direction },
                { autoAlpha: 1, x: 0, duration: 0.4, ease: "power2.out" }
            );

            // Animate in new image
            gsap.fromTo(newImgTarget,
                { autoAlpha: 0, x: -30 * direction },
                { autoAlpha: 1, x: 0, duration: 0.4, ease: "power2.out", delay: 0.1 }
            );

            // Animate in new content
            gsap.fromTo(newContent,
                { autoAlpha: 0, y: 30 },
                { autoAlpha: 1, y: 0, duration: 0.4, delay: 0.15 }
            );

            // Scroll to top
            overlay.scrollTop = 0;

            isTransitioning = false;
        }, 300);
    }

    /**
     * Go to previous project
     */
    function goToPrev() {
        if (activeIndex !== null && activeIndex > 0) {
            switchToProject(activeIndex - 1);
        }
    }

    /**
     * Go to next project
     */
    function goToNext() {
        if (activeIndex !== null && activeIndex < overlayItems.length - 1) {
            switchToProject(activeIndex + 1);
        }
    }

    /**
     * Close overlay (handles both current page and other page projects)
     */
    function closeOverlay() {
        if (activeIndex === null) return;

        const closingIndex = activeIndex;
        const localIndex = getLocalIndex(closingIndex);
        const onCurrentPage = isOnCurrentPage(closingIndex);
        activeIndex = null; // Reset early to prevent double-close

        const overlayItem = overlayItems[closingIndex];
        const titleWrap = overlayItem.querySelector("[data-overlay-text]");
        const imgWrap = overlayItem.querySelector("[data-overlay-img]");
        const content = overlayItem.querySelector(".projects-overlay-content");

        // Check if we have flipped elements from the current page
        const flippedTitleText = onCurrentPage ? titleWrap.querySelector(".projects-title-text:not(.projects-title-text--overlay)") : null;
        const flippedImage = onCurrentPage ? imgWrap.querySelector(".projects-img:not(.projects-img--overlay)") : null;

        // Hide nav
        gsap.to(overlayNav, { autoAlpha: 0, y: 20, duration: 0.3 });

        // Hide overlay content
        gsap.to(content, {
            autoAlpha: 0,
            y: 20,
            duration: 0.3
        });

        if (onCurrentPage && flippedTitleText && flippedImage && titleItems[localIndex]) {
            // Use Flip animation to return elements to list
            const titleBtn = titleItems[localIndex].querySelector(".projects-title-btn");
            const originalImgParent = imageItems[localIndex];

            // Record states BEFORE moving
            const titleState = Flip.getState(flippedTitleText, { props: "fontSize" });
            const imageState = Flip.getState(flippedImage);

            // Move elements back to original positions
            titleBtn.insertBefore(flippedTitleText, titleBtn.firstChild);
            originalImgParent.appendChild(flippedImage);

            // Show back the overlay's own title/image
            const overlayTitle = titleWrap.querySelector(".projects-title-text--overlay");
            const overlayImage = imgWrap.querySelector(".projects-img--overlay");
            if (overlayTitle) overlayTitle.style.display = "";
            if (overlayImage) overlayImage.style.display = "";

            // Animate back with Flip
            Flip.from(titleState, { duration: 0.6 });
            Flip.from(imageState, {
                duration: 0.6,
                onComplete: () => {
                    // Remove overlay classes after animation
                    overlay.classList.remove("is-active");
                    overlayItem.classList.remove("is-active");

                    // Show image stack after animation completes
                    if (imageStack) {
                        imageStack.classList.remove("is-hidden");
                    }

                    // Show the closed project's image (local index)
                    showImage(localIndex);
                }
            });
        } else {
            // For projects not on current page, just fade out and close
            gsap.to(titleWrap, { autoAlpha: 0, duration: 0.3 });
            gsap.to(imgWrap, { autoAlpha: 0, duration: 0.3 });

            setTimeout(() => {
                overlay.classList.remove("is-active");
                overlayItem.classList.remove("is-active");

                // Reset wrap visibility
                gsap.set(titleWrap, { autoAlpha: 1 });
                gsap.set(imgWrap, { autoAlpha: 1 });

                // Show image stack
                if (imageStack) {
                    imageStack.classList.remove("is-hidden");
                }

                // Show first image
                showImage(0);
            }, 300);
        }

        // First, immediately reset all title positions (in case we navigated between projects)
        titleItems.forEach((item) => {
            const text = item.querySelector(".projects-title-text");
            if (text) {
                // Immediately set to hidden state before animating
                gsap.set(text, { yPercent: 100, autoAlpha: 0 });
            }
        });

        // Show all titles with animation
        titleItems.forEach((item, i) => {
            const text = item.querySelector(".projects-title-text");
            if (text) {
                gsap.to(text, {
                    yPercent: 0,
                    autoAlpha: 1,
                    duration: 0.4,
                    delay: 0.3 + (i * 0.05)
                });
            }
        });

        // Restore body scroll and restart Lenis
        document.body.style.overflow = "";
        document.documentElement.style.overflow = "";
        if (window.lenis) {
            window.lenis.start();
            window.lenis.scrollTo(0, { immediate: true });
        } else {
            window.scrollTo(0, 0);
        }
    }

    // Event Listeners
    titleItems.forEach((item, index) => {
        // Hover - show corresponding image (local index)
        item.addEventListener("mouseenter", () => showImage(index));

        // Click - open overlay (use global index)
        const globalIndex = parseInt(item.dataset.globalIndex || index);
        item.addEventListener("click", () => openOverlay(globalIndex));
    });

    // Close on button click
    if (closeButton) {
        closeButton.addEventListener("click", closeOverlay);
    }

    // Prev/Next navigation
    if (prevButton) {
        prevButton.addEventListener("click", goToPrev);
    }
    if (nextButton) {
        nextButton.addEventListener("click", goToNext);
    }

    // Close on ESC key, navigate with arrow keys
    document.addEventListener("keydown", (e) => {
        if (activeIndex !== null) {
            if (e.key === "Escape") {
                closeOverlay();
            } else if (e.key === "ArrowLeft") {
                goToPrev();
            } else if (e.key === "ArrowRight") {
                goToNext();
            }
        }
    });

    // Close on overlay background click
    overlay?.addEventListener("click", (e) => {
        if (e.target === overlay) {
            closeOverlay();
        }
    });

    // Initialize first image as active
    resetImages();

    // Check URL for project to auto-open
    const urlParams = new URLSearchParams(window.location.search);
    const openSlug = urlParams.get('open');
    if (openSlug) {
        const loader = document.getElementById('pageLoader');

        // Find the project by slug in overlay items (they have all projects)
        overlayItems.forEach((item, globalIndex) => {
            if (item.dataset.slug === openSlug) {
                // Delay to allow page to fully load
                setTimeout(() => {
                    openOverlay(globalIndex, isOnCurrentPage(globalIndex));

                    // Hide loader after overlay opens
                    setTimeout(() => {
                        if (loader) {
                            loader.classList.add('is-hidden');
                        }
                        document.body.classList.remove('is-loading-project');
                    }, 300);
                }, 300);
            }
        });
    }
}
